import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import QuickreplyIcon from '@mui/icons-material/Quickreply';
import SearchIcon from '@mui/icons-material/Search';
import { CHAT_LIGHT_FIELD_SX } from './chatComposerStyles';

/**
 * @param {string[]} replies
 * @param {string} query
 */
function filterQuickReplies(replies, query) {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return replies;
  const terms = trimmed.split(/\s+/).filter(Boolean);
  return replies.filter((reply) => {
    const haystack = reply.toLowerCase();
    return terms.every((term) => haystack.includes(term));
  });
}

function previewLabel(text, maxLen = 72) {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLen) return normalized;
  return `${normalized.slice(0, maxLen - 1)}…`;
}

/**
 * Searchable quick-reply picker for Chat Operations agents.
 */
export default function QuickRepliesPicker({
  replies = [],
  disabled = false,
  onSelect,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState('');

  const safeReplies = useMemo(
    () =>
      (Array.isArray(replies) ? replies : []).filter(
        (line) => typeof line === 'string' && line.trim(),
      ),
    [replies],
  );

  const filtered = useMemo(
    () => filterQuickReplies(safeReplies, search),
    [safeReplies, search],
  );

  const open = Boolean(anchorEl);

  if (safeReplies.length === 0) return null;

  const handleOpen = (event) => {
    setSearch('');
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
  };

  const handlePick = (text) => {
    onSelect?.(text);
    handleClose();
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        disabled={disabled}
        onClick={handleOpen}
        startIcon={<QuickreplyIcon fontSize="small" />}
        sx={{
          borderRadius: 2,
          fontWeight: 600,
          textTransform: 'none',
          borderColor: '#cbd5e1',
          color: '#334155',
          backgroundColor: '#ffffff',
          '&:hover': {
            backgroundColor: '#f8fafc',
            borderColor: '#94a3b8',
          },
        }}
      >
        Quick replies
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: 'min(92vw, 380px)', sm: 380 },
              maxHeight: 420,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#ffffff',
              color: '#212121',
              border: '1px solid #e2e8f0',
              boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
            },
          },
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
            Quick replies
          </Typography>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search replies…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{ 'aria-label': 'Search quick replies' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                </InputAdornment>
              ),
            }}
            sx={CHAT_LIGHT_FIELD_SX}
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#64748b' }}>
            {filtered.length} of {safeReplies.length} shown
          </Typography>
        </Box>

        <List
          dense
          disablePadding
          sx={{
            overflow: 'auto',
            flex: 1,
            py: 0.5,
          }}
        >
          {filtered.length === 0 ? (
            <Typography variant="body2" sx={{ p: 2, color: '#64748b' }}>
              No quick replies match your search.
            </Typography>
          ) : (
            filtered.map((template, index) => (
              <ListItemButton
                key={`${index}-${template.slice(0, 24)}`}
                onClick={() => handlePick(template)}
                sx={{
                  alignItems: 'flex-start',
                  py: 1.25,
                  px: 1.5,
                  borderBottom: '1px solid #f1f5f9',
                  '&:hover': { backgroundColor: '#f8fafc' },
                }}
              >
                <ListItemText
                  primary={previewLabel(template)}
                  secondary={template.length > 72 ? template : null}
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: { color: '#1e293b', fontWeight: 500, whiteSpace: 'normal' },
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    sx: { color: '#64748b', whiteSpace: 'normal', mt: 0.5 },
                  }}
                />
              </ListItemButton>
            ))
          )}
        </List>
      </Popover>
    </>
  );
}
